import React from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    Badge,
    Image,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const News = () => {
    return (
        <Box position="relative" minH="100vh" display="flex" flexDirection="column">
            <NavbarWithCallToAction />
            
            {/* Background gradient */}
            <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                sx={{
                    background: `
                        radial-gradient(circle at 0% 0%, rgba(138, 43, 226, 0.15) 0%, transparent 50%),
                        radial-gradient(circle at 100% 0%, rgba(255, 20, 147, 0.15) 0%, transparent 50%),
                        radial-gradient(circle at 0% 100%, rgba(0, 191, 255, 0.2) 0%, transparent 50%),
                        radial-gradient(circle at 100% 100%, rgba(50, 205, 50, 0.15) 0%, transparent 50%)
                    `,
                    filter: "blur(60px)",
                }}
            />

            {/* Content container */}
            <Box
                position="relative"
                zIndex="1"
                flex="1"
                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                sx={{
                    background: `
                        linear-gradient(45deg, rgba(138, 43, 226, 0.05) 0%, transparent 20%),
                        linear-gradient(135deg, rgba(255, 20, 147, 0.05) 25%, transparent 45%),
                        linear-gradient(225deg, rgba(0, 191, 255, 0.1) 50%, transparent 75%),
                        linear-gradient(315deg, rgba(50, 205, 50, 0.05) 75%, transparent 100%)
                    `
                }}
            >
                <Container maxW="4xl" py={20}>
                    <VStack spacing={20} align="stretch">
                        {/* Title Section */}
                        <Box textAlign="center">
                            <Badge colorScheme="purple" fontSize="xl" p={4} borderRadius="full" mb={6}>
                                🎭 Week 6 Show & Tell 🎭
                            </Badge>
                            <Heading 
                                size="3xl" 
                                mb={8}
                                color="#6B46C1"
                                textShadow="2px 2px 4px rgba(0,0,0,0.1)"
                            >
                                If I Had a Superpower...
                            </Heading>
                            <Text color="gray.700" fontSize="2xl" fontWeight="bold">
                                By Alex Miller
                            </Text>
                        </Box>

                        {/* Introduction */}
                        <Box 
                            p={8} 
                            bg="white"
                            borderRadius="3xl"
                            boxShadow="0 20px 40px -12px rgba(0, 0, 0, 0.25)"
                            textAlign="center"
                            transform="rotate(-1deg)"
                            _hover={{ transform: "rotate(0deg)" }}
                            transition="all 0.3s ease"
                        >
                            <Text fontSize="4xl" mb={6}>🤔</Text>
                            <Text color="gray.700" fontSize="3xl" lineHeight="1.4" fontWeight="bold">
                                Do you want superpowers? I do
                            </Text>
                            <Text color="gray.700" fontSize="3xl" lineHeight="1.4" fontWeight="bold" mt={4}>
                                I know which ones I want
                            </Text>
                        </Box>

                        {/* Big Space for Suspense */}
                        <Box height="400px" />

                        {/* Main Superpower - Shapeshifting */}
                        <Box textAlign="center">
                            <Heading size="2xl" color="#EC4899" mb={8}>
                                My #1 Superpower Would Be...
                            </Heading>
                            
                            {/* Giant Shapeshifting Icon */}
                            <Box 
                                fontSize="15rem" 
                                mb={8}
                                sx={{
                                    animation: "morph 4s ease-in-out infinite",
                                }}
                            >
                                🦎➡️🦅➡️🐺
                            </Box>
                            
                            <Heading size="3xl" color="#6B46C1" mb={8} textShadow="2px 2px 4px rgba(0,0,0,0.1)">
                                SHAPESHIFTING 🎭
                            </Heading>
                        </Box>

                        {/* Why Shapeshifting is Cool */}
                        <VStack spacing={8}>
                            <Box 
                                p={6} 
                                bg="white"
                                borderRadius="2xl"
                                boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1)"
                                textAlign="center"
                                transform="rotate(1deg)"
                            >
                                <Text fontSize="5xl" mb={4}>🦅</Text>
                                <Text fontSize="2xl" color="gray.700" fontWeight="bold">
                                    I could be an eagle and fly in the sky
                                </Text>
                            </Box>

                            <Box 
                                p={6} 
                                bg="white"
                                borderRadius="2xl"
                                boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1)"
                                textAlign="center"
                                transform="rotate(-1deg)"
                            >
                                <Text fontSize="5xl" mb={4}>🦈</Text>
                                <Text fontSize="2xl" color="gray.700" fontWeight="bold">
                                    Or be a shark and swim in the ocean
                                </Text>
                            </Box>

                            <Box 
                                p={6} 
                                bg="white"
                                borderRadius="2xl"
                                boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1)"
                                textAlign="center"
                                transform="rotate(1deg)"
                            >
                                <Text fontSize="5xl" mb={4}>🦁</Text>
                                <Text fontSize="2xl" color="gray.700" fontWeight="bold">
                                    Maybe be a lion and be really strong
                                </Text>
                            </Box>
                        </VStack>

                        {/* Funny Joke Section */}
                        <Box 
                            p={8} 
                            bg="linear-gradient(135deg, #FFE4E1, #FFF0F5)"
                            borderRadius="3xl"
                            boxShadow="0 15px 35px -5px rgba(0, 0, 0, 0.15)"
                            textAlign="center"
                            border="3px dashed #FF69B4"
                        >
                            <Text fontSize="3xl" mb={4}>😂</Text>
                            <Text fontSize="xl" color="#D63384" fontWeight="bold" mb={2}>
                                Funny Joke Alert!
                            </Text>
                            <Text fontSize="lg" color="gray.700">
                                What do you call a shapeshifter who can&apos;t decide what to be?
                            </Text>
                            <Text fontSize="xl" color="#6B46C1" fontWeight="bold" mt={4}>
                                Con-FUSED! 🤪
                            </Text>
                        </Box>

                        {/* Plot Twist - Second Superpower */}
                        <Box textAlign="center">
                            <Text fontSize="2xl" color="#EC4899" mb={6}>
                                But wait... there&apos;s more 🎉
                            </Text>
                            <Heading size="xl" color="#6B46C1" mb={8}>
                                I also want a SECOND superpower...
                            </Heading>
                        </Box>

                        {/* Big Space for Second Superpower Suspense */}
                        <Box height="400px" />

                        {/* Teleportation Section */}
                        <Box textAlign="center">
                            <Heading size="2xl" color="#0EA5E9" mb={16}>
                                Superpower #2: TELEPORTATION ⚡
                            </Heading>
                            
                            {/* Giant Black Hole Image */}
                            <Box 
                                mb={8}
                                display="flex"
                                justifyContent="center"
                                position="relative"
                            >
                                {/* Real Black Hole Image */}
                                <Image
                                    src="/black_hole.webp"
                                    alt="Black hole for teleportation"
                                    width="400px"
                                    height="400px"
                                    borderRadius="full"
                                    objectFit="cover"
                                    boxShadow="0 0 100px rgba(138, 43, 226, 0.8), 0 0 200px rgba(138, 43, 226, 0.6)"
                                    sx={{
                                        animation: "slowSpin 10s linear infinite",
                                    }}
                                />
                                
                                {/* Commented out custom black hole code */}
                                {/* 
                                <Box
                                    position="absolute"
                                    width="500px"
                                    height="500px"
                                    borderRadius="full"
                                    border="4px solid transparent"
                                    background="linear-gradient(45deg, transparent, #8A2BE2, transparent, #6A1B9A, transparent)"
                                    sx={{
                                        animation: "antiGravity 8s linear infinite",
                                        zIndex: 1,
                                        "&::before": {
                                            content: '""',
                                            position: "absolute",
                                            top: "4px",
                                            left: "4px",
                                            right: "4px",
                                            bottom: "4px",
                                            borderRadius: "full",
                                            background: "transparent"
                                        }
                                    }}
                                />
                                
                                <Box
                                    width="400px"
                                    height="400px"
                                    borderRadius="full"
                                    background="radial-gradient(circle, #000000 15%, #0a0a0a 30%, #1a1a2e 50%, #16213e 70%, #533483 90%, #8A2BE2 100%)"
                                    position="relative"
                                    zIndex={2}
                                    sx={{
                                        animation: "hypnoticBlackHole 6s ease-in-out infinite",
                                        "&::before": {
                                            content: '""',
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            width: "60px",
                                            height: "60px",
                                            borderRadius: "full",
                                            background: "#000000",
                                            boxShadow: "0 0 30px rgba(0, 0, 0, 1)",
                                            animation: "centerPulse 4s ease-in-out infinite"
                                        },
                                        "&::after": {
                                            content: '""',
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            width: "450px",
                                            height: "450px",
                                            borderRadius: "full",
                                            background: "radial-gradient(circle, transparent 60%, rgba(138, 43, 226, 0.3) 80%, rgba(106, 27, 154, 0.2) 100%)",
                                            animation: "outerGlow 5s ease-in-out infinite",
                                            zIndex: -1
                                        }
                                    }}
                                />
                                */}
                            </Box>
                        </Box>

                        {/* Why Teleportation is Amazing */}
                        <VStack spacing={8}>
                            <Box 
                                p={6} 
                                bg="white"
                                borderRadius="2xl"
                                boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1)"
                                textAlign="center"
                                transform="rotate(-1deg)"
                            >
                                <Text fontSize="5xl" mb={4}>🏠➡️🏫</Text>
                                <Text fontSize="2xl" color="gray.700" fontWeight="bold">
                                    I would never be late for school
                                </Text>
                            </Box>

                            <Box 
                                p={6} 
                                bg="white"
                                borderRadius="2xl"
                                boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1)"
                                textAlign="center"
                                transform="rotate(1deg)"
                            >
                                <Text fontSize="5xl" mb={4}>🌍🚀🪐</Text>
                                <Text fontSize="2xl" color="gray.700" fontWeight="bold">
                                    I could go to space and see other planets
                                </Text>
                            </Box>

                            <Box 
                                p={6} 
                                bg="white"
                                borderRadius="2xl"
                                boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1)"
                                textAlign="center"
                                transform="rotate(-1deg)"
                            >
                                <Text fontSize="5xl" mb={4}>🍕</Text>
                                <Text fontSize="2xl" color="gray.700" fontWeight="bold">
                                    I could get pizza from anywhere I want
                                </Text>
                            </Box>
                        </VStack>

                        {/* Another Funny Joke */}
                        <Box 
                            p={8} 
                            bg="linear-gradient(135deg, #E0F2FE, #F0F9FF)"
                            borderRadius="3xl"
                            boxShadow="0 15px 35px -5px rgba(0, 0, 0, 0.15)"
                            textAlign="center"
                            border="3px dashed #0EA5E9"
                        >
                            <Text fontSize="3xl" mb={4}>🤣</Text>
                            <Text fontSize="xl" color="#0EA5E9" fontWeight="bold" mb={2}>
                                Another Joke Coming Through!
                            </Text>
                            <Text fontSize="lg" color="gray.700">
                                Why don&apos;t teleporters ever get lost?
                            </Text>
                            <Text fontSize="xl" color="#6B46C1" fontWeight="bold" mt={4}>
                                Because they always know where they&apos;re GOING! 📍
                            </Text>
                        </Box>

                        {/* Conclusion */}
                        <Box 
                            p={10} 
                            bg="linear-gradient(135deg, #FEF3C7, #FEF9E7)"
                            borderRadius="3xl"
                            boxShadow="0 20px 40px -12px rgba(0, 0, 0, 0.25)"
                            textAlign="center"
                            border="4px solid #F59E0B"
                        >
                            <Text fontSize="4xl" mb={6}>🌟</Text>
                            <Heading size="xl" color="#D97706" mb={6}>
                                The End of My News
                            </Heading>
                            <Text fontSize="lg" color="gray.700" mb={4}>
                                With shapeshifting AND teleportation, I could be anywhere, 
                                as anything, at any time
                            </Text>
                            <Text fontSize="xl" color="#6B46C1" fontWeight="bold">
                                What superpower would YOU choose 🤔✨
                            </Text>
                        </Box>
                    </VStack>
                </Container>
            </Box>

            <FooterWithFourColumns />

            {/* Add animations */}
            <style>{`
                @keyframes morph {
                    0% { transform: scale(1) rotate(0deg) }
                    25% { transform: scale(1.1) rotate(5deg) }
                    50% { transform: scale(1) rotate(0deg) }
                    75% { transform: scale(1.1) rotate(-5deg) }
                    100% { transform: scale(1) rotate(0deg) }
                }
                
                @keyframes slowSpin {
                    0% { transform: rotate(0deg) }
                    100% { transform: rotate(360deg) }
                }
                
                @keyframes float {
                    0% { transform: translateY(0px) }
                    50% { transform: translateY(-10px) }
                    100% { transform: translateY(0px) }
                }
                
                /* Commented out complex black hole animations
                @keyframes teleport {
                    0% { transform: scale(1) }
                    50% { transform: scale(1.2) rotate(180deg) }
                    100% { transform: scale(1) rotate(360deg) }
                }
                
                @keyframes hypnoticBlackHole {
                    0% { 
                        transform: scale(1) rotate(0deg);
                        box-shadow: 
                            0 0 50px rgba(138, 43, 226, 0.6),
                            0 0 100px rgba(138, 43, 226, 0.4),
                            0 0 150px rgba(138, 43, 226, 0.2),
                            inset 0 0 50px rgba(0, 0, 0, 0.9);
                    }
                    25% { 
                        transform: scale(1.1) rotate(90deg);
                        box-shadow: 
                            0 0 80px rgba(106, 27, 154, 0.8),
                            0 0 160px rgba(106, 27, 154, 0.6),
                            0 0 240px rgba(106, 27, 154, 0.4),
                            inset 0 0 80px rgba(0, 0, 0, 1);
                    }
                    50% { 
                        transform: scale(0.95) rotate(180deg);
                        box-shadow: 
                            0 0 100px rgba(138, 43, 226, 0.8),
                            0 0 200px rgba(138, 43, 226, 0.6),
                            0 0 300px rgba(138, 43, 226, 0.4),
                            inset 0 0 100px rgba(0, 0, 0, 1);
                    }
                    75% { 
                        transform: scale(1.05) rotate(270deg);
                        box-shadow: 
                            0 0 120px rgba(75, 0, 130, 0.8),
                            0 0 240px rgba(75, 0, 130, 0.6),
                            0 0 360px rgba(75, 0, 130, 0.4),
                            inset 0 0 120px rgba(0, 0, 0, 1);
                    }
                    100% { 
                        transform: scale(1) rotate(360deg);
                        box-shadow: 
                            0 0 50px rgba(138, 43, 226, 0.6),
                            0 0 100px rgba(138, 43, 226, 0.4),
                            0 0 150px rgba(138, 43, 226, 0.2),
                            inset 0 0 50px rgba(0, 0, 0, 0.9);
                    }
                }
                
                @keyframes centerPulse {
                    0% { transform: translate(-50%, -50%) scale(1); opacity: 1 }
                    50% { transform: translate(-50%, -50%) scale(1.3); opacity: 0.8 }
                    100% { transform: translate(-50%, -50%) scale(1); opacity: 1 }
                }
                
                @keyframes outerGlow {
                    0% { transform: translate(-50%, -50%) scale(1); opacity: 0.2 }
                    50% { transform: translate(-50%, -50%) scale(1.05); opacity: 0.5 }
                    100% { transform: translate(-50%, -50%) scale(1); opacity: 0.2 }
                }
                
                @keyframes antiGravity {
                    0% { transform: rotate(0deg) scale(1) }
                    25% { transform: rotate(90deg) scale(1.02) }
                    50% { transform: rotate(180deg) scale(1) }
                    75% { transform: rotate(270deg) scale(0.98) }
                    100% { transform: rotate(360deg) scale(1) }
                }
                */
            `}</style>
        </Box>
    );
};

export default News; 